import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Alert,
  Spinner,
  Card,
  Dropdown,
  ButtonGroup,
  Badge,
  OverlayTrigger,
  Tooltip,
  Form,
  InputGroup,
  Table,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  IconBuildingStore,
  IconRefresh,
  IconDownload,
  IconPlus,
  IconSearch,
  IconFilter,
  IconEye,
  IconEdit,
  IconPlayerPause,
  IconX,
  IconTrash,
  IconExternalLink,
  IconCalendar,
  IconUsers,
  IconTrendingUp,
  IconTrendingDown,
  IconStar,
  IconChartPie,
  IconCheck,
  IconDeviceAnalytics,
} from "@tabler/icons-react";

// Components
import PublicationStats from "./components/PublicationStats";
import PublicationFilters from "./components/PublicationFilters";
import PublicationCard from "./components/PublicationCard";
import PublicationPagination from "./components/PublicationPagination";
import DatatablePaginated from "../../jsx/components/DatatablePaginated";

// Services
import MarketplaceServices from "./services/MarketplaceServices";

// Styles
import "./marketplace.css";
import "./MarketplacePublications.mobile.css";

const MarketplacePublications = () => {
  const navigate = useNavigate();

  // States
  const [publications, setPublications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [sellerProfile, setSellerProfile] = useState({ hasProfile: false, loading: true });

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });

  // Filters
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    category: "all",
    page: 1,
    page_size: 10,
  });

  // Load publications using new API
  const loadPublications = useCallback(
    async (filterParams = filters) => {
      try {
        setLoading(true);
        setError(null);

        // Convert filters to API format (Django pagination)
        const params = {};

        if (filterParams.status && filterParams.status !== "all") {
          params.status = filterParams.status;
        }

        if (filterParams.search && filterParams.search.trim()) {
          params.search = filterParams.search.trim();
        }

        if (filterParams.category && filterParams.category !== "all") {
          params.category = filterParams.category;
        }

        // Use page-based pagination instead of offset/limit
        if (filterParams.page) {
          params.page = filterParams.page;
          params.page_size = filterParams.page_size || 10;
        }

        const response = await MarketplaceServices.listPublications(params);

        // Handle new API response format (Django pagination)
        if (response.data && response.data.results) {
          const publications = response.data.results || [];
          const count = response.data.count || 0;
          const next = response.data.next;
          const previous = response.data.previous;

          // Calculate stats from publications data
          const statusCounts = publications.reduce((acc, pub) => {
            acc[pub.status] = (acc[pub.status] || 0) + 1;
            return acc;
          }, {});

          const totalPublications = count;
          const publishedCount = statusCounts.published || 0;
          const pendingCount = statusCounts.pending || 0;
          const suspendedCount = statusCounts.suspended || 0;
          const rejectedCount = statusCounts.rejected || 0;
          const removedCount = statusCounts.removed || 0;

          // Set stats from calculated data
          setStats({
            summary: {
              total: totalPublications,
              published: publishedCount,
              pending: pendingCount,
              suspended: suspendedCount,
              rejected: rejectedCount,
              removed: removedCount,
            },
            total: totalPublications,
            percentages: {
              published:
                totalPublications > 0
                  ? Math.round((publishedCount / totalPublications) * 100)
                  : 0,
              pending:
                totalPublications > 0
                  ? Math.round((pendingCount / totalPublications) * 100)
                  : 0,
              suspended:
                totalPublications > 0
                  ? Math.round((suspendedCount / totalPublications) * 100)
                  : 0,
              rejected:
                totalPublications > 0
                  ? Math.round((rejectedCount / totalPublications) * 100)
                  : 0,
              removed:
                totalPublications > 0
                  ? Math.round((removedCount / totalPublications) * 100)
                  : 0,
            },
            // Additional stats calculated from publications
            total_downloads: publications.reduce(
              (sum, pub) => sum + (pub.download_count || 0),
              0
            ),
            total_views: publications.reduce(
              (sum, pub) => sum + (pub.view_count || 0),
              0
            ),
            average_rating:
              publications.length > 0
                ? publications.reduce(
                    (sum, pub) => sum + (pub.rating || 0),
                    0
                  ) / publications.length
                : 0,
            featured_agents: publications.filter((pub) => pub.featured).length,
          });

          // Format publications data for display
          const formattedPublications = publications.map((publication) => ({
            // IDs e Relacionamentos
            id: publication.id,
            agent_id: publication.agent_id,
            agent_slug: publication.agent_slug,
            seller_profile: publication.seller_profile,
            marketplace_product_id: publication.marketplace_product_id,
            marketplace_url: publication.marketplace_url,

            // Dados Básicos do Agente
            agent_name: publication.agent_name || "Nome não informado",
            agent_description: publication.agent_description || "",
            agent_area: publication.agent_job || "Área não informada",
            agent_job: publication.agent_job,

            // Status e Controle
            status: publication.status || "unknown",
            status_display: publication.status_display || publication.status,
            published_at: publication.published_at,
            rejection_reason: publication.rejection_reason,

            // Preço e Marketing
            price: parseFloat(publication.price) || 0,
            banner: publication.banner,
            banner_url: publication.banner_url,
            video_url: publication.video_url,
            description: publication.description,

            // Métricas
            featured: publication.featured || false,
            popularity_score: publication.popularity_score || 0,
            rating: publication.rating || 0,
            rating_count: publication.rating_count || 0,
            download_count: publication.download_count || 0,
            view_count: publication.view_count || 0,

            // Campos Avançados
            gallery_images: publication.gallery_images || [],
            pricing_info: publication.pricing_info || {},
            features: publication.features || [],
            capabilities: publication.capabilities || {},
            tags: publication.tags || [],
            technical_specs: publication.technical_specs || {},
            last_updated_content: publication.last_updated_content,

            // Plataforma e Instruções
            platform: publication.platform,
            instructions: publication.instructions,
            for_download: publication.for_download || false,

            // Informações da Empresa
            company_name: publication.company_name,
            company_logo: publication.company_logo,

            // Categorias
            category: publication.category || {},
            category_url: publication.category_url,
            seller_url: publication.seller_url,
            api_category_url: publication.api_category_url,
            api_seller_url: publication.api_seller_url,

            // Timestamps
            created_at: publication.created_at || new Date().toISOString(),
            updated_at: publication.updated_at || new Date().toISOString(),

            // Helper properties for UI
            statusVariant: MarketplaceServices.getStatusVariant(
              publication.status
            ),
            statusText: MarketplaceServices.getStatusText(publication.status),
            canEdit: MarketplaceServices.canEdit(publication.status),
            canPublish: MarketplaceServices.canPublish(publication.status),
            canSuspend: MarketplaceServices.canSuspend(publication.status),
            canRemove: MarketplaceServices.canRemove(publication.status),
            formattedCreatedAt: publication.created_at
              ? new Date(publication.created_at).toLocaleDateString("pt-BR")
              : "",
            formattedUpdatedAt: publication.updated_at
              ? new Date(publication.updated_at).toLocaleDateString("pt-BR")
              : "",
            formattedPublishedAt: publication.published_at
              ? new Date(publication.published_at).toLocaleDateString("pt-BR")
              : null,
            formattedPrice: publication.price
              ? new Intl.NumberFormat("pt-BR", {
                  style: "currency",
                  currency: publication.pricing_info?.currency || "BRL",
                }).format(parseFloat(publication.price))
              : "Gratuito",
          }));

          setPublications(formattedPublications);

          // Calculate pagination from Django pagination format
          const currentPageSize = filterParams.page_size || 10;
          const currentPage = filterParams.page || 1;
          const totalPages = Math.ceil(count / currentPageSize);

          setPagination({
            currentPage: currentPage,
            totalPages: totalPages,
            totalItems: count,
            itemsPerPage: currentPageSize,
            hasNext: !!next,
            hasPrevious: !!previous,
          });
        } else {
          throw new Error("Erro ao carregar publicações");
        }
      } catch (error) {
        console.error("Erro ao carregar agentes publicados:", error);
        setError("Erro ao carregar os agentes publicados. Tente novamente.");
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  // Load data using v1 API
  const loadData = useCallback(
    async (filterParams = filters) => {
      await loadPublications(filterParams);
    },
    [loadPublications, filters]
  );

  // Check seller profile
  const checkSellerProfile = useCallback(async () => {
    try {
      setSellerProfile({ hasProfile: false, loading: true });
      const response = await MarketplaceServices.loadSellerProfile();
      
      // Verificar se há perfil válido
      const hasValidProfile = response.data?.has_profile && response.data?.profile;
      setSellerProfile({ hasProfile: hasValidProfile, loading: false });
    } catch (error) {
      console.error('Erro ao verificar perfil do vendedor:', error);
      
      // Se erro 404, significa que não tem perfil
      if (error.response?.status === 404) {
        setSellerProfile({ hasProfile: false, loading: false });
      } else {
        setSellerProfile({ hasProfile: false, loading: false });
      }
    }
  }, []);

  // Initial load
  useEffect(() => {
    checkSellerProfile();
    loadData();
  }, [loadData, checkSellerProfile]);

  // Handle filter changes
  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    loadData(newFilters);
  };

  // Handle clear filters
  const handleClearFilters = () => {
    const clearedFilters = {
      search: "",
      status: "all",
      category: "all",
      page: 1,
      page_size: 10,
    };
    setFilters(clearedFilters);
    loadData(clearedFilters);
  };

  // Handle page change
  const handlePageChange = (page) => {
    const newFilters = { ...filters, page };
    setFilters(newFilters);
    loadData(newFilters);
  };

  // Handle refresh
  const handleRefresh = () => {
    loadData();
  };

  // Handle publish action
  const handlePublish = async (publicationId) => {
    const result = await Swal.fire({
      title: "Solicitar Publicação",
      text: "Deseja solicitar a publicação deste agente no marketplace?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, solicitar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#28a745",
    });

    if (!result.isConfirmed) return;

    try {
      setActionLoading(publicationId);
      await MarketplaceServices.publishPublication(publicationId);

      Swal.fire({
        icon: "success",
        title: "Solicitação Enviada",
        text: "Sua solicitação de publicação foi enviada para análise.",
        timer: 3000,
        showConfirmButton: false,
      });

      loadData();
    } catch (error) {
      console.error("Erro ao solicitar publicação:", error);

      let errorMessage = "Erro ao solicitar publicação.";
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      Swal.fire({
        icon: "error",
        title: "Erro",
        text: errorMessage,
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Handle suspend action
  const handleSuspend = async (publicationId) => {
    const result = await Swal.fire({
      title: "Suspender Publicação",
      text: "Deseja suspender temporariamente esta publicação?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, suspender",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#ffc107",
    });

    if (!result.isConfirmed) return;

    try {
      setActionLoading(publicationId);
      await MarketplaceServices.suspendPublication(publicationId);

      Swal.fire({
        icon: "success",
        title: "Publicação Suspensa",
        text: "A publicação foi suspensa temporariamente.",
        timer: 3000,
        showConfirmButton: false,
      });

      loadData();
    } catch (error) {
      console.error("Erro ao suspender publicação:", error);

      let errorMessage = "Erro ao suspender publicação.";
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      Swal.fire({
        icon: "error",
        title: "Erro",
        text: errorMessage,
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Handle remove action
  const handleRemove = async (publicationId) => {
    const result = await Swal.fire({
      title: "Remover Publicação",
      html: `
        <p>Deseja remover permanentemente esta publicação?</p>
        <p class="small text-muted">Esta ação não pode ser desfeita.</p>
      `,
      icon: "error",
      showCancelButton: true,
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
    });

    if (!result.isConfirmed) return;

    try {
      setActionLoading(publicationId);
      await MarketplaceServices.removePublication(publicationId);

      Swal.fire({
        icon: "success",
        title: "Publicação Removida",
        text: "A publicação foi removida permanentemente.",
        timer: 3000,
        showConfirmButton: false,
      });

      loadData();
    } catch (error) {
      console.error("Erro ao remover publicação:", error);

      let errorMessage = "Erro ao remover publicação.";
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      Swal.fire({
        icon: "error",
        title: "Erro",
        text: errorMessage,
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Handle export
  const handleExport = async (format = "csv") => {
    try {
      setLoading(true);

      const csvContent = await MarketplaceServices.exportPublications(
        format,
        filters
      );

      if (format === "csv") {
        MarketplaceServices.downloadCSV(
          csvContent,
          "minhas-publicacoes-marketplace.csv"
        );

        Swal.fire({
          icon: "success",
          title: "Exportação Concluída",
          text: "O arquivo foi baixado com sucesso.",
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error("Erro ao exportar:", error);

      Swal.fire({
        icon: "error",
        title: "Erro na Exportação",
        text: "Erro ao exportar os dados. Tente novamente.",
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setLoading(false);
    }
  };

  // Status helper functions
  const getStatusVariant = (status) => {
    const variants = {
      published: "success",
      pending: "primary",
      suspended: "warning",
      removed: "danger",
      rejected: "secondary",
    };
    return variants[status] || "secondary";
  };

  const getStatusText = (status) => {
    const texts = {
      published: "Publicado",
      pending: "Pendente",
      suspended: "Suspenso",
      removed: "Removido",
      rejected: "Rejeitado",
    };
    return texts[status] || status;
  };

  // Desktop DataTable columns configuration
  const columns = useMemo(
    () => [
      {
        name: "Agente",
        selector: (row) => row.agent_name || "Nome não informado",
        sortable: true,
        cell: (row) => (
          <div className="d-flex align-items-center gap-2">
            {row.banner_url ? (
              <img
                src={row.banner_url}
                alt="Banner"
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "8px",
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "8px",
                  background: "linear-gradient(135deg, #f8f9fa, #e9ecef)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconBuildingStore size={20} color="#6c757d" />
              </div>
            )}
            <div>
              <div style={{ fontWeight: "600", color: "var(--text-primary)" }}>
                {row.agent_name}
              </div>
              <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
                {row.agent_area}
              </div>
            </div>
          </div>
        ),
        grow: 2,
      },
      {
        name: "Status",
        selector: (row) => row.status,
        sortable: true,
        cell: (row) => (
          <div>
            <Badge bg={getStatusVariant(row.status)} className="mb-1">
              {getStatusText(row.status)}
            </Badge>
            {row.featured && (
              <div>
                <Badge bg="warning" size="sm">
                  <IconStar size={10} className="me-1" />
                  Destaque
                </Badge>
              </div>
            )}
          </div>
        ),
        width: "120px",
      },
      {
        name: "Preço",
        selector: (row) => row.price,
        sortable: true,
        cell: (row) => (
          <div style={{ fontWeight: "600", color: "var(--success)" }}>
            {row.formattedPrice}
          </div>
        ),
        width: "100px",
      },
      {
        name: "Métricas",
        cell: (row) => (
          <div style={{ fontSize: "12px" }}>
            <div className="d-flex align-items-center gap-1 mb-1">
              <IconEye size={12} />
              <span>{row.view_count}</span>
            </div>
            <div className="d-flex align-items-center gap-1">
              <IconDownload size={12} />
              <span>{row.download_count}</span>
            </div>
          </div>
        ),
        width: "120px",
      },
      {
        name: "Publicado em",
        selector: (row) => row.published_at,
        sortable: true,
        cell: (row) => (
          <div style={{ fontSize: "12px" }}>
            {row.formattedPublishedAt || (
              <span style={{ color: "var(--text-muted)" }}>-</span>
            )}
          </div>
        ),
        width: "140px",
      },
      {
        name: "Ações",
        cell: (row) => (
          <div className="d-flex gap-1">
            <OverlayTrigger overlay={<Tooltip>Ver agente</Tooltip>}>
              <Button
                variant="outline-primary"
                size="sm"
                className="px-2"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/marketplace/${row.id}`);
                }}
                disabled={actionLoading === row.id}
              >
                <IconEye size={14} />
              </Button>
            </OverlayTrigger>

            {row.canSuspend && (
              <OverlayTrigger overlay={<Tooltip>Suspender</Tooltip>}>
                <Button
                  variant="outline-warning"
                  size="sm"
                  className="px-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSuspend(row.id);
                  }}
                  disabled={actionLoading === row.id}
                >
                  <IconPlayerPause size={14} />
                </Button>
              </OverlayTrigger>
            )}

            {row.canRemove && (
              <OverlayTrigger overlay={<Tooltip>Remover</Tooltip>}>
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="px-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(row.id);
                  }}
                  disabled={actionLoading === row.id}
                >
                  <IconX size={14} />
                </Button>
              </OverlayTrigger>
            )}
          </div>
        ),
        width: "180px",
        ignoreRowClick: true,
      },
    ],
    [actionLoading]
  );

  // Filter configuration for search
  const filterByField = {
    fields: ["agent_name", "agent_area", "marketplace_product_id"],
    labels: ["Nome do Agente", "Área", "ID do Marketplace"],
  };

  // Handle marketplace URL view
  const handleViewMarketplace = (publication) => {
    if (publication.marketplace_url) {
      // Open marketplace URL in new tab
      window.open(publication.marketplace_url, "_blank", "noopener,noreferrer");
    } else {
      Swal.fire({
        icon: "info",
        title: "URL do Marketplace",
        text: "Este agente ainda não possui URL do marketplace disponível.",
        confirmButtonColor: "#17a2b8",
      });
    }
  };

  return (
    <Container fluid className="marketplace-publications-page py-4">
      {/* Header */}
      <div className="marketplace-header mpp-header-mobile mb-4">
        <Row className="align-items-center">
          <Col>
            <h1 className="h3 mb-1 fw-bold">
              <IconBuildingStore size={30} className="text-primary me-2" />
              Publicações no Marketplace
            </h1>
            <p className="text-muted mb-0">
              Gerencie suas publicações de agentes no marketplace
            </p>
          </Col>

          <Col xs="auto">
            <div className="mpp-header-actions">
              <ButtonGroup>
                <Button
                  variant="outline-secondary"
                  onClick={handleRefresh}
                  disabled={loading}
                  title="Atualizar"
                >
                  <IconRefresh size={16} />
                </Button>

               {/*  <Dropdown as={ButtonGroup}>
                  <Button
                    variant="outline-primary"
                    onClick={() => handleExport("csv")}
                    disabled={loading || publications.length === 0}
                  >
                    <IconDownload size={16} className="me-1" />
                    Exportar
                  </Button>

                  <Dropdown.Toggle
                    split
                    variant="outline-primary"
                    disabled={loading || publications.length === 0}
                  />

                  <Dropdown.Menu>
                    <Dropdown.Item onClick={() => handleExport("csv")}>
                      <IconDownload size={16} className="me-2" />
                      Exportar CSV
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown> */}

                {sellerProfile.hasProfile ? (
                  <Button as={Link} to="/marketplace/create" variant="primary">
                    <IconPlus size={16} className="me-1" />
                    Nova Publicação
                  </Button>
                ) : (
                  <Button as={Link} to="/marketplace/seller-profile" variant="warning">
                    <IconPlus size={16} className="me-1" />
                    Configurar Perfil
                  </Button>
                )}
              </ButtonGroup>
            </div>
          </Col>
        </Row>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          <Alert.Heading>
            <IconX size={20} className="me-2" />
            Erro
          </Alert.Heading>
          {error}
        </Alert>
      )}

      {/* Seller Profile Alert */}
      {!sellerProfile.loading && !sellerProfile.hasProfile && (
        <Alert variant="info" className="mb-4">
          <Alert.Heading>
            <IconPlus size={20} className="me-2" />
            Perfil de Vendedor Necessário
          </Alert.Heading>
          <p className="mb-2">
            Para publicar agentes no marketplace, você precisa primeiro configurar seu perfil de vendedor.
          </p>
          <Button as={Link} to="/marketplace/seller-profile" variant="primary" size="sm">
            Configurar Perfil de Vendedor
          </Button>
        </Alert>
      )}

      {/* Stats Cards - Enhanced Pattern from PublicationsList */}
      <section className="mpp-metrics-section mpp-stats-mobile mb-4">
        <div className="mpp-dashboard-grid mpp-grid-4">
          {!stats || loading
            ? Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className="mpp-metric-card mpp-loading-skeleton"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="mpp-metric-header">
                    <div
                      className="mpp-metric-icon"
                      style={{ background: "#f8f9fa" }}
                    >
                      <Spinner size="sm" />
                    </div>
                  </div>
                  <div className="mpp-metric-value">--</div>
                  <h3 className="mpp-metric-label">Carregando...</h3>
                </div>
              ))
            : [
                {
                  id: "published",
                  icon: IconCheck,
                  value: stats.summary?.published || 0,
                  label: "Publicados",
                  description: "Ativos no marketplace",
                  trend: "positive",
                  color: "linear-gradient(135deg, #2bc155, #20c997)",
                  bgColor: "success",
                },
                {
                  id: "pending",
                  icon: IconDeviceAnalytics,
                  value: stats.summary?.pending || 0,
                  label: "Pendentes",
                  description: "Aguardando aprovação",
                  trend: "neutral",
                  color: "linear-gradient(135deg, #137ff7, #1ea7c5)",
                  bgColor: "primary",
                },
                {
                  id: "suspended",
                  icon: IconPlayerPause,
                  value: stats.summary?.suspended || 0,
                  label: "Suspensos",
                  description: "Temporariamente inativos",
                  trend: "negative",
                  color: "linear-gradient(135deg, #ffc107, #ff8c00)",
                  bgColor: "warning",
                },
                {
                  id: "removed",
                  icon: IconX,
                  value: stats.summary?.removed || 0,
                  label: "Removidos",
                  description: "Removidos do marketplace",
                  trend: "negative",
                  color: "linear-gradient(135deg, #dc3545, #c82333)",
                  bgColor: "danger",
                },
              ].map((card, index) => {
                const IconComponent = card.icon;
                return (
                  <div
                    key={card.id}
                    className="mpp-metric-card fade-in"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="mpp-metric-header">
                      <div
                        className="mpp-metric-icon"
                        style={{ background: card.color }}
                      >
                        <IconComponent size={20} />
                      </div>
                      <div className={`mpp-metric-trend mpp-${card.trend}`}>
                        {card.trend === "positive" ? (
                          <IconTrendingUp size={10} />
                        ) : card.trend === "negative" ? (
                          <IconTrendingDown size={10} />
                        ) : (
                          <IconChartPie size={10} />
                        )}
                      </div>
                    </div>
                    <div className="mpp-metric-value">{card.value}</div>
                    <h3 className="mpp-metric-label">{card.label}</h3>
                    <p className="mpp-metric-description">{card.description}</p>
                  </div>
                );
              })}
        </div>
      </section>

      {/* Filters */}
      <div className="mpp-filters-mobile">
        <PublicationFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
          loading={loading}
        />
      </div>

      {/* Mobile/Desktop Container */}
      <div className="mpp-mobile-container">
        {/* Desktop DataTable */}
        <div className="mpp-datatable-wrapper">
          <Card>
            <Card.Header className="d-flex gap-2 align-items-center">
              <IconBuildingStore size={20} />
              <h5 className="mb-0">Lista de Publicações</h5>
            </Card.Header>
            <div>
              {loading && publications.length === 0 ? (
                <div
                  className="d-flex justify-content-center align-items-center"
                  style={{ minHeight: "200px" }}
                >
                  <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3">Carregando publicações...</p>
                  </div>
                </div>
              ) : publications.length > 0 ? (
                <DatatablePaginated
                  data={publications}
                  columns={columns}
                  totalRows={pagination.totalItems}
                  isLoading={loading}
                  handlePageChange={handlePageChange}
                  filterByField={filterByField}
                  filterDefaultField="agent_name"
                  searchBarType={1}
                />
              ) : (
                <Alert variant="info" className="text-center">
                  <IconBuildingStore size={24} className="mb-2" />
                  <p className="mb-0">
                    {filters.search ||
                    filters.status !== "all" ||
                    filters.category !== "all"
                      ? "Nenhuma publicação encontrada com os filtros aplicados."
                      : "Nenhuma publicação encontrada."}
                  </p>
                  <small>
                    {filters.search ||
                    filters.status !== "all" ||
                    filters.category !== "all"
                      ? "Tente ajustar os filtros de busca."
                      : "Publique seu primeiro agente no marketplace para começar."}
                  </small>
                </Alert>
              )}
            </div>
          </Card>
        </div>

        {/* Mobile Cards */}
        <div className="mpp-mobile-cards">
          {loading && publications.length === 0 ? (
            <div className="mpp-mobile-loader">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Carregando publicações...</p>
            </div>
          ) : publications.length === 0 ? (
            <div className="mpp-mobile-empty">
              <IconBuildingStore size={48} />
              <h3>Nenhuma publicação encontrada</h3>
              <p>
                {filters.search ||
                filters.status !== "all" ||
                filters.category !== "all"
                  ? "Nenhuma publicação encontrada com os filtros aplicados."
                  : "Você ainda não tem publicações no marketplace."}
              </p>
              {filters.search ||
              filters.status !== "all" ||
              filters.category !== "all" ? (
                <Button variant="outline-primary" onClick={handleClearFilters}>
                  <IconFilter size={16} className="me-1" />
                  Limpar Filtros
                </Button>
              ) : (
                sellerProfile.hasProfile ? (
                  <Button
                    as={Link}
                    to="/marketplace/create"
                    variant="primary"
                    size="lg"
                  >
                    <IconPlus size={16} className="me-2" />
                    Criar Primeira Publicação
                  </Button>
                ) : (
                  <Button
                    as={Link}
                    to="/marketplace/seller-profile"
                    variant="warning"
                    size="lg"
                  >
                    <IconPlus size={16} className="me-2" />
                    Configurar Perfil de Vendedor
                  </Button>
                )
              )}
            </div>
          ) : (
            <>
              {publications.map((publication, index) => (
                <div
                  key={publication.id}
                  className={`mpp-publication-card mpp-fade-in`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  {/* Header */}
                  <div className="mpp-card-header">
                    <div className="mpp-banner-container">
                      {publication.banner_url ? (
                        <img
                          src={publication.banner_url}
                          alt="Banner"
                          className="mpp-banner-image"
                        />
                      ) : (
                        <IconBuildingStore
                          size={24}
                          className="mpp-banner-placeholder"
                        />
                      )}
                    </div>
                    <div className="mpp-publication-info">
                      <h4 className="mpp-agent-name">
                        {publication.agent_name}
                      </h4>
                      <p className="mpp-agent-description">
                        {publication.agent_description}
                      </p>
                      <div className="mpp-price-badge">
                        {publication.formattedPrice}
                      </div>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="mpp-status-container">
                    <Badge
                      bg={getStatusVariant(publication.status)}
                      className="mpp-status-badge"
                    >
                      {getStatusText(publication.status)}
                    </Badge>
                    {publication.featured && (
                      <div className="mpp-featured-badge">
                        <IconStar size={10} className="me-1" />
                        Destaque
                      </div>
                    )}
                  </div>

                  {/* Body */}
                  <div className="mpp-card-body">
                    <div className="mpp-info-grid">
                      <div className="mpp-info-item">
                        <div className="mpp-info-label">Visualizações</div>
                        <div className="mpp-info-value">
                          <IconEye size={14} />
                          {publication.view_count}
                        </div>
                      </div>
                      <div className="mpp-info-item">
                        <div className="mpp-info-label">Downloads</div>
                        <div className="mpp-info-value">
                          <IconDownload size={14} />
                          {publication.download_count}
                        </div>
                      </div>
                    </div>

                    {publication.rejection_reason && (
                      <div className="mpp-rejection-alert">
                        <p className="mpp-rejection-text">
                          <IconX size={12} />
                          {publication.rejection_reason}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Meta Info */}
                  <div className="mpp-meta-info">
                    <div className="mpp-date-info">
                      <IconCalendar size={12} />
                      <span>
                        {publication.formattedPublishedAt || "Não publicado"}
                      </span>
                    </div>
                    {publication.marketplace_url && (
                      <a
                        href={publication.marketplace_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mpp-marketplace-link"
                      >
                        <IconExternalLink size={12} />
                        Ver no marketplace
                      </a>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="mpp-card-actions">
                    <Button
                      variant="info"
                      className="mpp-action-btn"
                      onClick={() => navigate(`/marketplace/${publication.id}`)}
                      disabled={actionLoading === publication.id}
                    >
                      <IconEye size={14} />
                      Ver Agente
                    </Button>

                    <Button
                      variant="primary"
                      className="mpp-action-btn"
                      onClick={() => handleViewMarketplace(publication)}
                      disabled={actionLoading === publication.id}
                    >
                      <IconExternalLink size={14} />
                      Marketplace
                    </Button>

                    {publication.canSuspend && (
                      <Button
                        variant="warning"
                        className="mpp-action-btn"
                        onClick={() => handleSuspend(publication.id)}
                        disabled={actionLoading === publication.id}
                      >
                        <IconPlayerPause size={14} />
                        Suspender
                      </Button>
                    )}

                    {publication.canRemove && (
                      <Button
                        variant="danger"
                        className="mpp-action-btn"
                        onClick={() => handleRemove(publication.id)}
                        disabled={actionLoading === publication.id}
                      >
                        <IconX size={14} />
                        Remover
                      </Button>
                    )}
                  </div>
                </div>
              ))}

              {/* Mobile Pagination */}
              <div className="mpp-mobile-pagination">
                <div className="mpp-pagination-info">
                  Página {pagination.currentPage} de {pagination.totalPages}
                </div>
                <div className="mpp-pagination-buttons">
                  <Button
                    variant="secondary"
                    className="mpp-pagination-btn"
                    disabled={pagination.currentPage === 1}
                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="secondary"
                    className="mpp-pagination-btn"
                    disabled={pagination.currentPage >= pagination.totalPages}
                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                  >
                    Próxima
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Container>
  );
};

export default MarketplacePublications;
